// @flow
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import mergeWith from 'lodash/mergeWith';
import merge from 'lodash/merge';
import isArray from 'lodash/isArray';
import { cleanupFormValues } from 'hooks/affordaPortalFormState/utils.formStateCleanup';
import type { SectionNames } from 'constants/sectionNames';

// Track mounted fields across form updates
const mountedFieldsHistory = {};

const safeGet = (obj, key) =>
  obj && obj.hasOwnProperty(key) ? obj[key] : undefined;

const getNestedValue = (obj, path) => {
  const keys = path.split('.');
  let current = obj;

  for (const key of keys) {
    current = safeGet(current, key); // Safely retrieve the next level
    if (!current) break; // Exit early if undefined or null
    if (current.hasOwnProperty('_f')) {
      const refValue = current._f?.ref?.value;
      let value = current._f?.value;

      if (!value?.length && refValue?.length) {
        value = refValue;
      }

      return value;
    }
  }

  return current?.value; // Return value if it exists at the final level
};

const getBaseFieldName = (fieldName: string) => {
  if (!fieldName.includes('.')) return fieldName;
  const fieldNameArray = fieldName.split('.');

  return fieldNameArray[fieldNameArray.length - 1];
};

const getLabel = ({ label }) => {
  if (Boolean(label?.props?.id?.length)) {
    return window.app.intl.getFormattedMessageInEnglish(label.props);
  } else {
    return label;
  }
};

const getDisplayedValue = ({ optionLabel, value, variant }) => {
  if (variant === 'date' && value?._isAMomentObject) return formatDateDB(value);
  return optionLabel ?? value;
};

const getValue = ({ value, variant }) => {
  if (variant === 'date' && value?._isAMomentObject) return formatDateDB(value);
  return value;
};

const getOptionLabel = ({ options, value }) => {
  if (!options?.length) return null;

  if (Array.isArray(value)) {
    return options
      .filter((option) => value.includes(option.value))
      .map((option) => getLabel({ label: option.label ?? option.text }));
  } else {
    const selectedOption = options.find((option) => option.value === value);
    return getLabel({
      label: selectedOption?.label ?? selectedOption?.text,
    });
  }
};

const getItem = ({
  baseFieldName,
  label,
  value,
  variant,
  optionLabel,
  isAffordaPortalSpecific,
}) => {
  return {
    name: baseFieldName,
    label: getLabel({ label }),
    value: getValue({ value, variant }),
    displayedValue: getDisplayedValue({ optionLabel, value, variant }),
    isAffordaPortalSpecific: isAffordaPortalSpecific ?? false,
  };
};

const getFieldsArrayAndMap = (fields = []) => {
  const fieldsArray = Array.isArray(fields)
    ? fields
    : Object.values(fields).flatMap((field) => field);

  const fieldsMap = fieldsArray.reduce((acc, field) => {
    acc[field.name] = field;
    return acc;
  }, {});

  return {
    fieldsArray,
    fieldsMap,
  };
};

/**
 * Returns a mapping of field names that are currently mounted in the form,
 * organized by their parent object paths and mapped to the ID of the item they're related to.
 * Also maintains history of mounted fields across form updates.
 *
 * @param {Object} control - React Hook Form's control object
 * @param fieldsArray
 * @param {string} [sectionName] - The current section name for flat controls
 * @returns {Object} - Object mapping parent paths to arrays of mounted field names
 */
const getMountedFieldNames = ({ control, fieldsArray, sectionName }) => {
  const controlFields = Object.values(control._fields);

  if (!controlFields?.length) {
    return sectionName ? { [sectionName]: [] } : {};
  }

  const filterAndMapMountedFieldNames = (controlFields) =>
    controlFields
      .filter((field) => {
        const isMounted = field._f?.mount;
        return isMounted;
      })
      .map((field) => field._f.name);

  const isNestedControl = !controlFields[0].hasOwnProperty('_f');

  let result = {};

  if (isNestedControl) {
    // Handle nested controls (like sections with multiple items)
    controlFields.forEach((fields, index) => {
      const fieldsValues = Object.values(fields);
      const isNestedControl = !fieldsValues[0].hasOwnProperty('_f');
      const parentKey = Object.keys(control._fields)[index];

      if (isNestedControl) {
        // Recursively process deeper nested controls
        const nestedResult = getMountedFieldNames({
          control: {
            _fields: fields,
          },
          fieldsArray,
          sectionName: parentKey,
        });

        // Merge the nested results with the current result
        Object.entries(nestedResult).forEach(([key, fieldNames]) => {
          // Map to the actual item ID if it's in the format "sectionName.itemId"
          let fullKey;

          if (key.includes('.')) {
            // If key already contains a dot, it's already a full path
            fullKey = key;
          } else {
            // For nested fields, we need to extract the actual item ID
            // The parentKey is typically in the format "sectionName" and key is the index
            // We need to find the actual item ID from the fields
            const nestedFields = Object.keys(fields);

            if (nestedFields.length > Number(key)) {
              const actualItemId = nestedFields[Number(key)];
              fullKey = `${parentKey}.${actualItemId}`;
            } else {
              // Fallback to the original behavior if we can't find the actual item ID
              fullKey = `${parentKey}.${key}`;
            }
          }

          result[fullKey] = fieldNames;
        });
      } else {
        // Get mounted field names for this control
        const mountedFields = filterAndMapMountedFieldNames(fieldsValues);

        // Store the field names with their parent key
        result[parentKey] = mountedFields;
      }
    });
  } else {
    const itemKey = fieldsArray?.[0]?.id;
    // Handle flat controls - assign them to the provided section name
    const mountedFields = filterAndMapMountedFieldNames(controlFields);
    if (itemKey) {
      result[itemKey] = mountedFields;
    } else if (sectionName) {
      result[sectionName] = mountedFields;
    } else {
      // If no section name is provided, use a default key (can be modified as needed)
      result.defaultSection = mountedFields;
    }
  }

  // Process each key in the current result to update history
  Object.entries(result).forEach(([key, fields]) => {
    if (!mountedFieldsHistory[key]) {
      mountedFieldsHistory[key] = [];
    }

    // Add new mounted fields to history
    fields.forEach((field) => {
      if (!mountedFieldsHistory[key].includes(field)) {
        mountedFieldsHistory[key].push(field);
      }
    });
  });

  // Return only currently mounted fields
  return result;
};

const buildNewFormValues = ({ control, mountedFieldNames, fieldsMap }) => {
  const newFormValues = {};

  // Process all mounted fields from all sections
  Object.entries(mountedFieldNames).forEach(([section, fieldNames]) => {
    fieldNames.forEach((fieldName) => {
      const baseFieldName = getBaseFieldName(fieldName);
      if (fieldsMap[fieldName]) {
        const {
          id,
          label,
          options,
          variant,
          readOnly,
          isAffordaPortalSpecific,
        } = fieldsMap[fieldName];

        // only process fields that are not [readOnly]
        if (!readOnly) {
          const value = getNestedValue(control._fields, fieldName);
          const optionLabel = getOptionLabel({ options, value });

          const item = getItem({
            baseFieldName,
            label,
            optionLabel,
            value,
            variant,
            isAffordaPortalSpecific,
          });

          if (id) {
            newFormValues[id] = {
              ...newFormValues[id],
              [baseFieldName]: item,
            };
          } else {
            newFormValues[baseFieldName] = item;
          }
        }
      }
    });
  });

  return newFormValues;
};

export const getNewFormState = ({ fields, newState = {}, prevState = {} }) => {
  const { fieldsArray } = getFieldsArrayAndMap(fields);

  const readOnlyFieldNames =
    fieldsArray?.filter(({ readOnly }) => readOnly)?.map(({ name }) => name) ??
    [];

  const customizer = (objValue: any, srcValue: any, key: string) => {
    if (readOnlyFieldNames.includes(key)) {
      return objValue; // Prioritize the value from the first object (prevState)
    }

    // Handle arrays: Use newState if the lengths differ
    if (isArray(objValue) && isArray(srcValue)) {
      if (objValue.length !== srcValue.length) {
        return srcValue; // Use newState array if lengths differ
      }
      return srcValue ?? objValue; // Otherwise, preserve newState
    }

    if (srcValue && typeof srcValue === 'object') {
      if (srcValue._isAMomentObject) {
        return formatDateDB(srcValue);
      } else {
        // Recursively process nested objects and arrays
        const newValue = isArray(srcValue) ? [] : {};
        let hasChanges = false; // Flag to track if any changes were made

        for (const k in srcValue) {
          const nestedValue = customizer(objValue?.[key]?.[k], srcValue[k], k);
          newValue[k] = nestedValue;
          if (nestedValue !== srcValue[k]) {
            hasChanges = true; // Set flag if a change occurred
          }
        }
        return hasChanges ? newValue : srcValue; // Return original if no changes
      }
    }

    return srcValue !== undefined ? srcValue : objValue;
  };

  return mergeWith({}, prevState, newState, customizer);
};

export const getNewFormValues = ({
  control,
  fields,
  prevValues,
  newFormState,
  stepsToCleanup,
  fieldNamesMap,
  sectionName,
  logger,
}: {
  sectionName: SectionNames,
}) => {
  if (!control || !fields) return prevValues ?? {};

  const { fieldsArray, fieldsMap } = getFieldsArrayAndMap(fields);

  const mountedFieldNames = getMountedFieldNames({
    control,
    fieldsArray,
    sectionName,
  });
  logger('Mounted field names:', mountedFieldNames);

  const newFormValues = buildNewFormValues({
    mountedFieldNames,
    fieldsMap,
    control,
  });
  logger('New form values:', newFormValues);

  logger('Before cleanup:', {
    prevValuesKeys: Object.keys(prevValues || {}),
    newFormValuesKeys: Object.keys(newFormValues),
  });

  // Use the consolidated cleanup function
  const cleanedPrevValues = cleanupFormValues({
    values: prevValues || {},
    fieldsArray,
    mountedFieldNames,
    newFormState,
    stepsToCleanup,
    fieldNamesMap,
    logger,
  });

  logger('After cleanup:', {
    cleanedPrevValuesKeys: Object.keys(cleanedPrevValues),
    cleanedPrevValues,
  });

  const mergedResult = merge({}, cleanedPrevValues, newFormValues);

  logger('Final merged result:', {
    mergedResultKeys: Object.keys(mergedResult),
    mergedResult,
  });

  return mergedResult;
};

export const buildMap = (steps, PATHS_TO_FIELD_NAMES_MAP) => {
  return steps
    .filter(({ optional }) => optional)
    .reduce((acc, step) => {
      // if step.name is a query string, remove it
      const id = step.name.includes('?') ? step.name.split('?')[0] : step.name;
      if (PATHS_TO_FIELD_NAMES_MAP[id]) {
        acc[step.name] = PATHS_TO_FIELD_NAMES_MAP[id];
      }

      return acc;
    }, {});
};
